import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, Bell, MapPin, ChevronDown, Zap, Tag, Home, Sparkles, Building2, BedDouble, PanelTop, Droplet, Briefcase, Store, Users, Wrench, Sofa, ArrowRight, Star, ShieldCheck, Clock, RefreshCw, CalendarDays, Trophy, Check } from 'lucide-react';
import { useApp } from '../context/AppContext';
import { ASSETS } from '../mock/data';
import BottomNav from '../components/BottomNav';
import DesktopHeader from '../components/DesktopHeader';

const iconMap = { Home, Sparkles, Building2, BedDouble, PanelTop, Droplet, Briefcase, Store, Users, Wrench, Zap, Sofa };

const FEATURED = ['home-cleaning', 'deep-cleaning', 'water-tank-cleaning', 'electrician-service'];

const CustomerHome = () => {
  const nav = useNavigate();
  const { user, address, cart, categories, services, offers } = useApp();
  const firstName = (user?.name || 'Guest').split(' ')[0];
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);

  return (
    <div className="min-h-screen bg-neutral-50 pb-24 md:pb-8">
      <DesktopHeader />

      {/* Mobile Header */}
      <div className="md:hidden px-5 pt-5 pb-3" style={{ background: 'linear-gradient(180deg, #FFF7C2 0%, #FFFFFF 100%)' }}>
        <div className="flex items-start justify-between">
          <div>
            <img src={ASSETS.logo} alt="Clean24" className="h-14 object-contain" />
            <button className="flex items-center gap-1 mt-1 text-sm text-neutral-700">
              <MapPin className="w-3.5 h-3.5 text-[#2E7D32]" />
              <span className="font-medium">{address}</span>
              <ChevronDown className="w-3.5 h-3.5" />
            </button>
          </div>
          <div className="flex gap-2">
            <button onClick={() => { if (window.confirm('Reload the latest catalog?')) { localStorage.removeItem('c24_categories'); localStorage.removeItem('c24_services'); localStorage.removeItem('c24_offers'); localStorage.removeItem('c24_data_version'); window.location.reload(); } }} title="Refresh catalog" className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center hover:bg-neutral-50 transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
            <button onClick={() => nav('/cart')} className="relative w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center">
              <ShoppingCart className="w-4 h-4" />
              {cartCount > 0 && <span className="absolute -top-1 -right-1 bg-[#EA580C] text-white text-[10px] font-bold rounded-full w-4 h-4 flex items-center justify-center">{cartCount}</span>}
            </button>
            <button className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center">
              <Bell className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {/* Compact greeting + hero */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 pt-3 md:pt-6">
        <div className="hidden md:flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-extrabold text-neutral-900">Hi, {firstName} <span>👋</span></h2>
            <p className="text-sm text-neutral-500 mt-0.5">What would you like cleaned today?</p>
          </div>
          <div className="flex items-center gap-4 text-xs text-neutral-600">
            <div className="flex items-center gap-1"><ShieldCheck className="w-4 h-4 text-[#2E7D32]" /> Verified pros</div>
            <div className="flex items-center gap-1"><Clock className="w-4 h-4 text-[#2E7D32]" /> 30-min express</div>
            <div className="flex items-center gap-1"><Star className="w-4 h-4 text-[#F5B301] fill-[#F5B301]" /> 4.9 rated</div>
          </div>
        </div>
        <h2 className="md:hidden text-2xl font-extrabold text-neutral-900">Hi, {firstName} <span>👋</span></h2>

        {/* Compact hero banner */}
        <div className="relative mt-4 md:mt-5 rounded-2xl overflow-hidden shadow-md h-40 md:h-48">
          <img src={ASSETS.team} alt="team" className="w-full h-full object-cover object-center" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/45 to-transparent" />
          <div className="absolute inset-0 px-5 md:px-8 flex flex-col justify-center max-w-2xl">
            <span className="inline-flex self-start items-center gap-1 bg-[#2E7D32] text-white text-[11px] md:text-xs font-semibold px-2.5 py-1 rounded-full">
              <Zap className="w-3 h-3 fill-white" /> 30-Min Express
            </span>
            <p className="text-white text-lg md:text-2xl font-bold leading-tight mt-2">Cleaner at your door in 30 minutes</p>
            <p className="text-white/85 text-xs md:text-sm mt-1">Verified pros • Live tracking • Cash or online</p>
            <button onClick={() => nav('/services/home-cleaning')} className="mt-3 self-start inline-flex items-center gap-1.5 bg-white text-neutral-900 text-sm font-semibold px-4 py-2 rounded-full hover:bg-neutral-100 transition-colors">
              Book now <ArrowRight className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </div>

      {/* Quick access: wallet / rewards / referral */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 mt-4 md:mt-5">
        <div className="grid grid-cols-3 gap-2 md:gap-3">
          <button data-testid="home-quick-wallet" onClick={() => nav('/wallet')} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-2 hover:border-[#2E7D32] transition-colors text-left">
            <div className="w-9 h-9 rounded-lg bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
              <Wrench className="hidden" /><span className="text-[#2E7D32] font-bold">₹</span>
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-neutral-900">Wallet</p>
              <p className="text-[10px] text-neutral-500 truncate">Pay from balance</p>
            </div>
          </button>
          <button data-testid="home-quick-rewards" onClick={() => nav('/rewards')} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-2 hover:border-[#D97706] transition-colors text-left">
            <div className="w-9 h-9 rounded-lg bg-[#FEF3C7] flex items-center justify-center flex-shrink-0">
              <Star className="w-4 h-4 text-[#D97706] fill-[#D97706]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-neutral-900">Rewards</p>
              <p className="text-[10px] text-neutral-500 truncate">Earn on every booking</p>
            </div>
          </button>
          <button data-testid="home-quick-referral" onClick={() => nav('/referral')} className="bg-white border border-neutral-200 rounded-xl p-3 flex items-center gap-2 hover:border-[#0891B2] transition-colors text-left">
            <div className="w-9 h-9 rounded-lg bg-[#CFFAFE] flex items-center justify-center flex-shrink-0">
              <Users className="w-4 h-4 text-[#0891B2]" />
            </div>
            <div className="min-w-0">
              <p className="text-xs font-bold text-neutral-900">Refer & earn</p>
              <p className="text-[10px] text-neutral-500 truncate">Give ₹100, get ₹100</p>
            </div>
          </button>
        </div>
      </div>

      {/* Offers row */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-base md:text-lg font-bold text-neutral-900">Offers for you</h3>
          <button className="text-xs text-[#2E7D32] font-semibold hover:underline">View all</button>
        </div>
        <div className="flex md:grid md:grid-cols-3 gap-3 overflow-x-auto md:overflow-visible -mx-5 md:mx-0 px-5 md:px-0 pb-2 scrollbar-hide">
          {offers.map((o) => (
            <div key={o.id} className="min-w-[240px] md:min-w-0 rounded-xl bg-gradient-to-br from-[#E8F5E9] to-[#F1F8E9] border border-[#C8E6C9] p-3.5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2E7D32] text-white text-[10px] font-bold flex items-center justify-center flex-shrink-0 text-center leading-tight px-1">{o.discount.replace(' OFF', '')}<br/>OFF</div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-neutral-900 truncate">{o.text}</p>
                <p className="text-[11px] text-neutral-600 mt-0.5">Code <span className="font-mono font-bold text-[#2E7D32] bg-white px-1.5 py-0.5 rounded border border-dashed border-[#2E7D32]">{o.code}</span></p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Subscription plans - 3 column grid */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 mt-6">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <h3 className="text-base md:text-lg font-bold text-neutral-900">Save with subscription plans</h3>
            <p className="text-xs text-neutral-500 mt-0.5">Regular cleaning at discounted rates</p>
          </div>
          <button onClick={() => nav('/subscriptions')} className="text-xs md:text-sm font-semibold text-[#2E7D32] hover:underline">See all →</button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
          {/* Weekly */}
          <PlanCard
            testid="home-plan-weekly"
            icon={CalendarDays}
            tag="Popular"
            name="Weekly Plan"
            price="375"
            period="week"
            perks={['4 visits / month', '3 hrs per visit', 'Same cleaner every time']}
            accent="#2E7D32"
            bg="#E8F5E9"
            onView={() => nav('/subscription/weekly')}
          />
          {/* Monthly */}
          <PlanCard
            testid="home-plan-monthly"
            icon={CalendarDays}
            tag="Best value"
            name="Monthly Plan"
            price="2799"
            period="month"
            perks={['8 visits / month', 'Priority slot booking', '10% off extra bookings']}
            accent="#2E7D32"
            bg="#E8F5E9"
            onView={() => nav('/subscription/monthly')}
          />
          {/* Gold */}
          <PlanCard
            testid="home-plan-gold"
            icon={Trophy}
            tag="Premium"
            name="Gold Membership"
            price="599"
            period="month"
            perks={['10% off ALL bookings', 'Priority + 24/7 support', '1 free deep-clean / year']}
            accent="#EA580C"
            bg="#FEF3C7"
            premium
            onView={() => nav('/subscription/gold')}
          />
        </div>
      </div>

      {/* Services grid - dense */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 mt-6">
        <div className="flex items-baseline justify-between mb-3">
          <h3 className="text-base md:text-lg font-bold text-neutral-900">All Services</h3>
          <p className="text-xs text-neutral-500">Pick a category to get started</p>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-3">
          {categories.map((c) => {
            const Icon = iconMap[c.icon] || Sparkles;
            return (
              <button key={c.id} onClick={() => nav(`/services/${c.id}`)}
                data-testid={`home-category-${c.id}`}
                className="bg-white border border-neutral-200 rounded-xl p-3 text-left hover:shadow-md hover:border-[#2E7D32] transition-all group">
                <div className="relative h-24 md:h-28 rounded-lg overflow-hidden bg-neutral-100">
                  <img src={c.image} alt={c.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                  <div className="absolute top-1.5 left-1.5 w-7 h-7 rounded-full bg-white shadow flex items-center justify-center">
                    <Icon className="w-3.5 h-3.5 text-[#2E7D32]" />
                  </div>
                </div>
                <p className="mt-2 text-xs md:text-sm font-bold text-neutral-900 leading-tight">{c.name}</p>
                <p className="text-[10px] md:text-[11px] text-neutral-500 mt-0.5">{c.tag} · from ₹{c.price}</p>
              </button>
            );
          })}
        </div>
      </div>

      {/* Featured Services */}
      <div className="max-w-[1200px] mx-auto px-5 md:px-6 mt-8">
        <h3 className="text-base md:text-lg font-bold text-neutral-900 mb-3">Popular services</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {FEATURED.flatMap((catId) => (services[catId]?.items || []).slice(0, 1).map((item) => ({ ...item, catId }))).map((item) => (
            <div key={item.id} className="bg-white border border-neutral-200 rounded-xl overflow-hidden hover:shadow-md transition-shadow">
              <div className="relative h-32">
                <img src={item.image} alt={item.name} className="w-full h-full object-cover" />
                <div className="absolute top-2 right-2 bg-white/95 backdrop-blur text-[10px] font-bold text-[#2E7D32] px-2 py-0.5 rounded-full">Save {item.save}%</div>
              </div>
              <div className="p-3">
                <p className="font-bold text-sm leading-tight line-clamp-1">{item.name}</p>
                <div className="flex items-center gap-1 mt-1">
                  <Star className="w-3 h-3 fill-[#F5B301] text-[#F5B301]" />
                  <span className="text-xs font-semibold">{item.rating}</span>
                  <span className="text-[10px] text-neutral-500">({item.reviews})</span>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-baseline gap-1">
                    <span className="text-xs text-neutral-400 line-through">₹{item.oldPrice}</span>
                    <span className="text-base font-extrabold text-[#EA580C]">₹{item.price}</span>
                  </div>
                  <button onClick={() => nav(`/services/${item.catId}`)} className="text-xs font-bold text-[#EA580C] border border-[#EA580C] px-3 py-1 rounded-full hover:bg-[#FFF7ED] transition-colors">Add</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <BottomNav />
    </div>
  );
};

export default CustomerHome;

const PlanCard = ({ testid, icon: Icon, tag, name, price, period, perks, accent, bg, premium, onView }) => {
  return (
    <div data-testid={testid} className={`relative bg-white rounded-2xl border ${premium ? 'border-[#EA580C]' : 'border-neutral-200'} p-4 md:p-5 flex flex-col hover:shadow-lg hover:-translate-y-0.5 transition-all`}>
      {tag && (
        <span className={`self-start text-[10px] font-bold px-2 py-0.5 rounded uppercase tracking-wider mb-2 ${premium ? 'bg-[#EA580C] text-white' : 'bg-[#E8F5E9] text-[#2E7D32]'}`}>{tag}</span>
      )}
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center flex-shrink-0" style={{ background: bg }}>
          <Icon className="w-5 h-5" style={{ color: accent }} />
        </div>
        <div>
          <p className="font-extrabold text-neutral-900 leading-tight">{name}</p>
          <p className="text-xs text-neutral-500">Cancel any time</p>
        </div>
      </div>
      <div className="mt-3 flex items-baseline gap-1">
        <span className="text-2xl md:text-3xl font-extrabold" style={{ color: accent }}>₹{price}</span>
        <span className="text-sm text-neutral-500">/{period}</span>
      </div>
      <ul className="mt-3 space-y-1.5">
        {perks.map((p, i) => (
          <li key={i} className="flex items-start gap-2 text-xs md:text-sm text-neutral-700">
            <Check className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" style={{ color: accent }} />
            <span>{p}</span>
          </li>
        ))}
      </ul>
      <button
        data-testid={`${testid}-view-btn`}
        onClick={onView}
        className={`mt-4 w-full py-2.5 rounded-lg font-semibold text-sm transition-colors ${premium ? 'bg-[#EA580C] hover:bg-[#C2410C] text-white' : 'bg-[#2E7D32] hover:bg-[#276D2A] text-white'}`}
      >
        View details
      </button>
    </div>
  );
};
